import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Icon, Typography } from '@scale/scaleui';
import { SquadType } from '@scale/shared/types/expertManagement';
import { Link } from 'react-router-dom';
import { ToolsLink } from 'react-scale/components/ExpertManagement/ExpertManagementPage/ToolsLink';
import { SQUAD_TYPE_TO_NAME_MAP } from 'react-scale/components/ExpertManagement/constants';
import { Routes, buildRoute } from 'react-scale/components/ExpertManagement/urls';
import { useRemoAdminCtx } from 'react-scale/components/remoAdmin/remoAdminContext';
import { UserInfo } from './types';

interface SquadInfoCardProps {
  userInfo: UserInfo;
  squadType?: SquadType;
}

export const SquadInfoCard: React.FC<SquadInfoCardProps> = (props: SquadInfoCardProps) => {
  const {
    id,
    manager,
    workerSlackId,
    expertSlackId,
    teamLeadPodType,
    expertSlackChannels,
    remoSlackChannels,
    squadName,
    squadLeaders,
  } = props.userInfo;

  const renderField = (label: string, value: string | JSX.Element, key?: string) => (
    <div className="flex justify-between" key={key}>
      <Typography.Subtitle3 className="my-auto">{label}</Typography.Subtitle3>
      <Typography.Subtitle3 className="my-auto">{value}</Typography.Subtitle3>
    </div>
  );

  const { isRemoAdmin } = useRemoAdminCtx();
  const isLocalEnvironment = () =>
    typeof window !== 'undefined' && window.location.hostname === 'localhost';

  return (
    <div className="flex flex-col gap-2 bg-neutral-0 p-4 border border-neutral-200 rounded-md w-[400px]">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon hasBackground className="bg-neutral-100" icon={['far', 'user']} />
          <Typography.Subtitle2>Basic Information</Typography.Subtitle2>
        </div>
        <div className="flex">
          <ToolsLink to={`/lookup/${id}`} className="flex items-center">
            <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-1" />
          </ToolsLink>
        </div>
      </div>
      {renderField('Squad Name', squadName)}
      {renderField(
        'Leaders',
        <div className="flex flex-col">
          {squadLeaders.map(manager => (
            <div key={manager.id} className="flex flex-col">
              <Typography.Paragraph3 className=" flex justify-end">
                {manager.name}
              </Typography.Paragraph3>
              <Typography.Paragraph3 className=" flex justify-end">
                ({manager.email})
              </Typography.Paragraph3>
            </div>
          ))}
        </div>,
      )}
      {renderField('Pod Type', teamLeadPodType?.name ?? 'N/A')}
      {props.squadType &&
        renderField('Squad Type', SQUAD_TYPE_TO_NAME_MAP[props.squadType] ?? 'N/A')}
      {workerSlackId && (
        <div className="flex flex-col gap-2">
          {renderField(
            'Remo Slack',
            <a
              href={`https://remotasks-community.slack.com/team/${workerSlackId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center ml-1"
            >
              Profile <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-1" />
            </a>,
          )}
          {remoSlackChannels.length > 0 &&
            remoSlackChannels.map((channel, idx) =>
              renderField(
                'Remo Slack Channel',
                <a
                  href={`https://remotasks-community.slack.com/channels/${channel.channel_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center ml-1 my-auto"
                  key={channel.channel_id}
                >
                  {`#${channel?.channel} `}
                  <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-1" />
                </a>,
                'remo-slack-channel-' + idx,
              ),
            )}
        </div>
      )}
      {expertSlackId && (
        <div className="flex flex-col gap-2">
          {renderField(
            'Expert Slack',
            <a
              href={`https://expert--community.slack.com/team/${expertSlackId}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center ml-1"
            >
              Profile <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-1" />
            </a>,
          )}
          {expertSlackChannels.length > 0 &&
            expertSlackChannels.map((channel, idx) =>
              renderField(
                'Expert Slack Channel',
                <a
                  href={`https://expert--community.slack.com/channels/${channel.channel_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center ml-1 my-auto"
                  key={channel.channel_id}
                >
                  {`#${channel?.channel} `}
                  <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-1" />
                </a>,
                'expert-slack-channel-' + idx,
              ),
            )}
        </div>
      )}
      {!isRemoAdmin &&
        renderField(
          'Expert Dashboard',
          <a
            href={
              isLocalEnvironment()
                ? `http://localhost:3002/en/expert?spoof=${id}`
                : `https://app.outlier.ai/en/expert?spoof=${id}`
            }
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center ml-1"
          >
            Spoof <FontAwesomeIcon icon="external-link-alt" className="text-xs ml-1" />
          </a>,
        )}
      {manager &&
        renderField(
          'Manager',
          <Link to={buildRoute(Routes.ExpertManagement, { user: manager.id })}>
            {manager.name}
          </Link>,
        )}
    </div>
  );
};
